import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Calendar, Clock, ChevronDown, Plus, 
  FileText, CheckSquare, AlertTriangle, 
  Search, Filter, Download, 
  XCircle, Tag, User
} from 'lucide-react';

const API_BASE_URL = 'https://ior.org/risk-register/web/api';

const MeetingIntegration = () => {
  // State for meetings management
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedView, setSelectedView] = useState('upcoming');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedMeeting, setExpandedMeeting] = useState(null);

  // State for meeting creation/editing
  const [isAddingMeeting, setIsAddingMeeting] = useState(false);

  // State for risk assignment
  const [isAddingRisk, setIsAddingRisk] = useState(false);
  const [selectedMeetingForRisk, setSelectedMeetingForRisk] = useState(null);
  const [availableRisks, setAvailableRisks] = useState([]);
  const [selectedRisk, setSelectedRisk] = useState('');

  const { user } = useAuth();
  const token = localStorage.getItem('authToken');

  // Fetch meetings and their details
  const fetchMeetings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `${API_BASE_URL}/meetings.php?status=${selectedView}&search=${searchTerm}`
      );
      const data = await response.json();

      if (data.success) {
        setMeetings(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch meetings');
      }
    } catch (err) {
      console.error('Error fetching meetings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch meeting details when expanded
  const fetchMeetingDetails = async (meetingId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/meetings.php?id=${meetingId}`);
      const data = await response.json();

      if (data.success) {
        setMeetings(current => 
          current.map(meeting => 
            meeting.meeting_code === meetingId
              ? { ...meeting, ...data.data }
              : meeting
          )
        );
      } else {
        throw new Error(data.error || 'Failed to fetch meeting details');
      }
    } catch (err) {
      console.error('Error fetching meeting details:', err);
      setError(err.message);
    }
  };

  // Fetch available risks for assignment
  const fetchAvailableRisks = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/risks.php?status=active&approval_level=pending`);
      const data = await response.json();
      
      if (data.success) {
        setAvailableRisks(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch available risks');
      }
    } catch (err) {
      console.error('Error fetching available risks:', err);
      setError(err.message);
    }
  };

  // Effect for fetching meetings
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchMeetings();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [selectedView, searchTerm]);

  // Effect for fetching meeting details when expanded
  useEffect(() => {
    if (expandedMeeting) {
      fetchMeetingDetails(expandedMeeting);
    }
  }, [expandedMeeting]);

  // Add new meeting
  const handleAddMeeting = async (meetingData) => {
    try {
      setError(null);
      const response = await fetch(`${API_BASE_URL}/meetings.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...meetingData,
          status: 'upcoming'
        }),
      });

      const data = await response.json();

      if (data.success) {
        await fetchMeetings();
        setIsAddingMeeting(false);
      } else {
        throw new Error(data.error || 'Failed to create meeting');
      }
    } catch (err) {
      console.error('Error creating meeting:', err);
      setError(err.message);
    }
  };

  // Add risk to meeting
  const handleAddRisk = async (meetingId) => {
    setSelectedMeetingForRisk(meetingId);
    setIsAddingRisk(true);
    await fetchAvailableRisks();
  };

  // Submit risk to meeting
  const handleRiskSubmit = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/meeting-risks.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          meeting_id: selectedMeetingForRisk,
          risk_id: selectedRisk,
          requires_decision: true,
          added_by: user.id
        }),
      });

      const data = await response.json();
      if (data.success) {
        if (expandedMeeting) {
          await fetchMeetingDetails(expandedMeeting);
        }
        setIsAddingRisk(false);
        setSelectedRisk('');
        setSelectedMeetingForRisk(null);
      } else {
        throw new Error(data.error || 'Failed to assign risk');
      }
    } catch (err) {
      console.error('Error assigning risk:', err);
      setError(err.message);
    }
  };

  // Record decision for a risk
  const handleRecordDecision = async (meetingId, riskId, decision, notes) => {
    try {
      const meeting = meetings.find(m => m.id === meetingId);
      const isSMT = meeting.type === 'SMT';

      // Record the decision
      const response = await fetch(`${API_BASE_URL}/meeting-decisions.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          meeting_id: meetingId,
          risk_id: riskId,
          decision,
          notes,
          decided_by: user.id,
          meeting_type: meeting.type,
          next_level: isSMT && decision === 'approved' ? 'GB' : null,
          is_final: !isSMT
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Update risk status
        const newStatus = isSMT 
          ? (decision === 'approved' ? 'pending-gb' : 'rejected')
          : (decision === 'approved' ? 'resolved' : 'rejected');

        await fetch(`${API_BASE_URL}/risks.php`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: riskId,
            approval_status: newStatus,
            status: !isSMT && decision === 'approved' ? 'resolved' : 'active',
            updated_by: 1, // TODO: Replace with actual user ID
            update_details: `${meeting.type} ${decision} with notes: ${notes}`
          })
        });

        // Update meeting status if all risks have decisions
        const hasAllDecisions = meeting.assigned_risks.every(risk => 
          risk.id === riskId || risk.decision
        );

        if (hasAllDecisions) {
          await fetch(`${API_BASE_URL}/meetings.php`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: meetingId,
              status: 'completed'
            })
          });
        }

        // Refresh meeting details
        await fetchMeetingDetails(meeting.meeting_code);
      } else {
        throw new Error(data.error || 'Failed to record decision');
      }
    } catch (err) {
      console.error('Error recording decision:', err);
      setError(err.message);
    }
  };

  // Generate meeting agenda
  const handleGenerateAgenda = async (meetingCode) => {
    try {
      setError(null);
      const response = await fetch(
        `${API_BASE_URL}/meetings.php?id=${meetingCode}&format=agenda`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/pdf'
          },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `meeting-agenda-${meetingCode}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate agenda');
      }
    } catch (err) {
      console.error('Error generating agenda:', err);
      setError(err.message);
    }
  };

  // Risk Badge Component
  // Risk Badge Component
  const RiskBadge = ({ risk }) => {
    const statusColors = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };

    const getStatusColor = (score) => {
      if (score >= 16) return statusColors.high;
      if (score >= 9) return statusColors.medium;
      return statusColors.low;
    };

    const getDecisionColor = (decision) => {
      if (!decision) return '';
      return decision.toLowerCase() === 'approved' 
        ? 'bg-green-100 text-green-800' 
        : 'bg-red-100 text-red-800';
    };

    return (
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(risk.risk_score)}`}>
              Score: {risk.risk_score}
            </span>
            <Tag className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500">{risk.category_label}</span>
          </div>
          <div className="mt-1 font-medium">{risk.title}</div>
          <div className="text-sm text-gray-500 flex items-center">
            <User className="w-4 h-4 mr-1" />
            {risk.owner_name}
          </div>
        </div>
        {risk.decision && (
          <div className="ml-4 text-sm">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDecisionColor(risk.decision)}`}>
              {risk.decision.toUpperCase()}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Meeting Card Component
  const MeetingCard = ({ meeting, onAddRisk, onRecordDecision, onGenerateAgenda }) => {
    const isExpanded = expandedMeeting === meeting.meeting_code;
    const toggleExpand = () => setExpandedMeeting(isExpanded ? null : meeting.meeting_code);

    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-4">
          {/* Meeting Header */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded text-sm font-medium ${
                  meeting.type === 'SMT' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                }`}>
                  {meeting.type}
                </span>
                <span className="text-lg font-medium">{meeting.title}</span>
              </div>
              <div className="mt-1 text-sm text-gray-500">
                <div className="flex items-center space-x-4">
                  <span className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(meeting.date).toLocaleDateString()}
                  </span>
                  <span className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {meeting.time}
                  </span>
                  <span>{meeting.location}</span>
                </div>
              </div>
            </div>
            <button
              onClick={toggleExpand}
              className="text-gray-400 hover:text-gray-600"
            >
              <ChevronDown className={`w-5 h-5 transform transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`} />
            </button>
          </div>

          {/* Expanded Content */}
          {isExpanded && (
            <div className="mt-4 pt-4 border-t">
              <div className="space-y-4">
                {/* Assigned Risks Section */}
                <div>
                  <h3 className="font-medium mb-2">Assigned Risks</h3>
                  <div className="space-y-2">
                    {meeting.assigned_risks?.length > 0 ? (
                      meeting.assigned_risks.map(risk => (
                        <div key={risk.risk_code}>
                          <RiskBadge risk={risk} />
                          {meeting.status === 'upcoming' && risk.requires_decision && !risk.decision && (
                            <div className="mt-2">
                              <DecisionForm
                                risk={risk}
                                onSubmit={(decision, notes) => 
                                  onRecordDecision(meeting.id, risk.id, decision, notes)
                                }
                              />
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-500">No risks assigned to this meeting</div>
                    )}
                  </div>
                </div>

                {/* Actions Section */}
                {meeting.status === 'upcoming' && (
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => onAddRisk(meeting.id)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Risk
                    </button>
                    <button
                      onClick={() => onGenerateAgenda(meeting.meeting_code)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Agenda
                    </button>
                  </div>
                )}

                {/* Decisions Section */}
                {meeting.status === 'completed' && meeting.decisions?.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-2">Decisions Made</h3>
                    <div className="space-y-2">
                      {meeting.decisions.map(decision => (
                        <div key={decision.id} className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Risk {decision.risk_code}</span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              decision.decision === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {decision.decision == 'approved' ? 'Resolved': decision.decision.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{decision.notes}</p>
                          <div className="text-sm text-gray-500 mt-1">
                            Decided by {decision.decided_by} on {new Date(decision.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // New Meeting Form Component
const NewMeetingForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    type: 'SMT',
    title: '',
    date: '',
    time: '',
    location: '',
    notes: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-medium mb-4">Schedule New Meeting</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Meeting Type</label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="SMT">SMT Meeting</option>
            <option value="GB">Governing Body Meeting</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="e.g., Monthly Risk Review Meeting"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Date</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              min={new Date().toISOString().split('T')[0]}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Time</label>
            <input
              type="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Location</label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="e.g., Board Room, Online Meeting"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="3"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="Any additional meeting notes or agenda items"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Schedule Meeting
          </button>
        </div>
      </form>
    </div>
  );
};

  // Decision Form Component
  const DecisionForm = ({ risk, onSubmit }) => {
    const [decision, setDecision] = useState('');
    const [notes, setNotes] = useState('');

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(decision, notes);
      setDecision('');
      setNotes('');
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700">Decision</label>
          <select
            value={decision}
            onChange={(e) => setDecision(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          >
            <option value="">Select decision</option>
            <option value="approved">Resolved</option>
            <option value="rejected">Reject</option>
            <option value="deferred">Defer</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            rows="2"
            required
          />
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
          >
            Save Decision
          </button>
        </div>
      </form>
    );
  };

  // Render Search and Filters
  const renderSearchAndFilters = () => (
    <div className="mb-6 flex space-x-4">
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Search meetings..."
          className="pl-10 pr-4 py-2 w-full border rounded-md"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <select
        className="px-4 py-2 border rounded-md"
        value={selectedView}
        onChange={(e) => setSelectedView(e.target.value)}
      >
        <option value="upcoming">Upcoming Meetings</option>
        <option value="completed">Completed Meetings</option>
        <option value="all">All Meetings</option>
      </select>
      <button 
        onClick={() => setIsAddingMeeting(true)}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
      >
        <Plus className="w-4 h-4 mr-2" />
        New Meeting
      </button>
    </div>
  );

  // Add Risk Modal
  const renderAddRiskModal = () => {
    if (!isAddingRisk) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-lg">
          <h2 className="text-lg font-medium mb-4">Add Risk to Meeting</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-400 text-red-700">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                {error}
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Risk
            </label>
            <select
              value={selectedRisk}
              onChange={(e) => setSelectedRisk(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select a risk...</option>
              {availableRisks.map(risk => (
                <option key={risk.id} value={risk.id}>
                  {risk.risk_code} - {risk.title}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setIsAddingRisk(false);
                setSelectedRisk('');
                setError(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRiskSubmit}
              disabled={!selectedRisk}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Risk
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Main Render Method
  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Meeting Management</h1>
        <div className="text-gray-600">
          Schedule and track risk reviews in SMT and GB meetings
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* Add Risk Modal */}
      {renderAddRiskModal()}

      {/* New Meeting Form */}
      {isAddingMeeting ? (
        <NewMeetingForm
          onSubmit={handleAddMeeting}
          onCancel={() => setIsAddingMeeting(false)}
        />
      ) : (
        <>
          {/* Search and Filters */}
          {renderSearchAndFilters()}
          
          {/* Meeting List */}
          <div className="space-y-4">
            {loading ? (
              <div className="flex justify-center items-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : meetings.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No meetings found
              </div>
            ) : (
              meetings
                .filter(meeting => 
                  selectedView === 'all' || 
                  (selectedView === 'upcoming' && meeting.status === 'upcoming') ||
                  (selectedView === 'completed' && meeting.status === 'completed')
                )
                .map(meeting => (
                  <MeetingCard
                    key={meeting.meeting_code}
                    meeting={meeting}
                    onAddRisk={handleAddRisk}
                    onRecordDecision={handleRecordDecision}
                    onGenerateAgenda={handleGenerateAgenda}
                  />
                ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Export Component
export default MeetingIntegration;
